/**
 * Background sync queue (optimistic UI, docs/PERF.md "Optimistic UI"): the app changes its local state at once and
 * records an OPERATION here; the client flushes operations to the API in the background, persists the queue
 * (IndexedDB) so nothing is lost when the app is closed offline, retries failures with exponential backoff + jitter,
 * DEDUPES by key (ten taps on a heart = one request carrying the latest state) and MERGES payloads of the same key with
 * a per-kind merge function (e.g. drawing meta: last write wins per field). Server conflicts are resolved by the
 * existing merge functions (progress: core `mergeProgress` via the server's merged answer; creatures:
 * `mergeCreatureLists`). Pure and deterministic (time is passed in): unit-tested in core.
 */

export interface SyncOp<P = unknown> {
  /** Unique id (for logs / tests). */
  id: string;
  /** What to send (`progress`, `profile`, `lang`, `drawingMeta`, `creatures`, `comment`, …). */
  kind: string;
  /** Dedupe key: a newer op with the same key replaces / merges into the queued one. */
  key: string;
  payload: P;
  /** ISO time of the first enqueue (kept on merges: the op keeps its place in the queue). */
  createdAt: string;
  /** ISO time of the last change. */
  updatedAt: string;
  attempts: number;
  /** Epoch ms before which the op is not retried. */
  nextAt: number;
  /** Last error (short). */
  error?: string;
}

export type SyncQueue = SyncOp[];

/** Result of sending an op. `retry` = transient (offline, 5xx, timeout); `drop` = permanent (4xx other than 408/429). */
export type SendResult = { ok: true } | { ok: false; retry: boolean; error: string };

export const SYNC_BACKOFF_BASE_MS = 1000;
export const SYNC_BACKOFF_MAX_MS = 5 * 60_000;
/** After this many failed attempts an op is dropped (it is retried for ≈ a day before that). */
export const SYNC_MAX_ATTEMPTS = 30;

/** Exponential backoff with ±20 % jitter (`rand` in [0, 1)); capped. */
export function backoffMs(attempts: number, rand = 0.5): number {
  const base = Math.min(SYNC_BACKOFF_MAX_MS, SYNC_BACKOFF_BASE_MS * 2 ** Math.max(0, attempts - 1));
  return Math.round(base * (0.8 + 0.4 * rand));
}

/** Merge two payloads of the same key: per-kind function, else the newer payload wins. */
export type PayloadMerge = (older: unknown, newer: unknown) => unknown;
/** Shallow last-write-wins merge of object payloads (drawing meta, profile fields). */
export const mergeFields: PayloadMerge = (a, b) => (a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b) ? { ...(a as object), ...(b as object) } : b);

/**
 * Add an op (immutable). An op with the same key is replaced in place (its position, createdAt kept) with the merged
 * payload; attempts reset so the new state goes out promptly.
 */
export function enqueueOp(q: SyncQueue, op: Pick<SyncOp, 'id' | 'kind' | 'key' | 'payload'>, nowMs: number, merge?: PayloadMerge): SyncQueue {
  const now = new Date(nowMs).toISOString();
  const i = q.findIndex((x) => x.key === op.key);
  if (i < 0) return [...q, { ...op, createdAt: now, updatedAt: now, attempts: 0, nextAt: nowMs }];
  const prev = q[i]!;
  const next: SyncOp = { ...prev, id: op.id, kind: op.kind, payload: merge ? merge(prev.payload, op.payload) : op.payload, updatedAt: now, attempts: 0, nextAt: nowMs, error: undefined };
  return q.map((x, k) => (k === i ? next : x));
}

/** Ops ready to send now, oldest first. */
export const dueOps = (q: SyncQueue, nowMs: number): SyncOp[] => q.filter((o) => o.nextAt <= nowMs).sort((a, b) => a.createdAt.localeCompare(b.createdAt));

/** Earliest retry time (ms) of a waiting op, or null when nothing waits. */
export const nextWakeAt = (q: SyncQueue): number | null => (q.length ? Math.min(...q.map((o) => o.nextAt)) : null);

/**
 * Apply a send result. Success removes the op ONLY if it was not changed meanwhile (`sent.updatedAt`); a newer state
 * stays queued and goes out next. Transient failures back off; permanent ones (or too many attempts) drop the op.
 */
export function settleOp(q: SyncQueue, sent: SyncOp, res: SendResult, nowMs: number, rand = 0.5): { queue: SyncQueue; dropped?: SyncOp } {
  const cur = q.find((o) => o.key === sent.key);
  if (!cur) return { queue: q };
  const changed = cur.updatedAt !== sent.updatedAt;
  if (res.ok) return { queue: changed ? q : q.filter((o) => o !== cur) };
  if (changed) return { queue: q }; // newer state queued: it will be sent as is
  const attempts = cur.attempts + 1;
  if (!res.retry || attempts >= SYNC_MAX_ATTEMPTS) return { queue: q.filter((o) => o !== cur), dropped: { ...cur, attempts, error: res.error } };
  const next: SyncOp = { ...cur, attempts, nextAt: nowMs + backoffMs(attempts, rand), error: res.error.slice(0, 160) };
  return { queue: q.map((o) => (o === cur ? next : o)) };
}

/** HTTP status → retry decision (network errors: status 0). */
export const isRetryableStatus = (status: number) => status === 0 || status === 408 || status === 425 || status === 429 || status >= 500;

/** Drop malformed entries of a persisted queue (older app versions, corrupted storage). */
export function normalizeQueue(x: unknown): SyncQueue {
  if (!Array.isArray(x)) return [];
  return x.filter(
    (o): o is SyncOp =>
      !!o && typeof o === 'object' && typeof (o as SyncOp).key === 'string' && typeof (o as SyncOp).kind === 'string' && typeof (o as SyncOp).createdAt === 'string' && Number.isFinite((o as SyncOp).nextAt),
  ).map((o) => ({ ...o, attempts: Number.isFinite(o.attempts) ? o.attempts : 0 }));
}
